import React, { useCallback, useRef, useState } from 'react';
import { useLiveMessageActions } from './useLiveMessages';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useLocale } from '@/app/user-and-session/languages/i18n/locale-provider';
import { getCurrentModelPrice } from '../data/chatSetting/settingOptions';
import { liveMessageDataHub } from '../data/liveMessageData/liveMessageDataHub';
import { useGlobalChatState } from './useGlobalChatState';
import { parseHints } from '../frontend/shared/textUtil';

interface UseSendText2APIParams {
  avatarId: string;
  avatarName: string;
  characterPrompt: string;
  userPreferredName: string;
}

/**
 * Unified hook for sending and receiving text messages to/from API backend
 * Handles both message sending and streaming response reception
 * Replaces LiveKit-based communication with direct API calls
 */
export function useSendText2API({
  avatarId,
  avatarName,
  characterPrompt,
  userPreferredName
}: UseSendText2APIParams) {
  const messageActions = useLiveMessageActions();
  const { userEmail, userName, isAuthenticated, isLoading: authLoading } = useAuth();
  const localeContext = useLocale();
  // Ensure locale is valid for backend API (must be 'en' or 'zh-Hans')
  // Fallback to 'en' if locale is undefined or invalid
  const locale: 'en' | 'zh-Hans' = (localeContext?.locale === 'en' || localeContext?.locale === 'zh-Hans') 
    ? localeContext.locale 
    : 'en';
  const [globalChatStateData, globalChatStateActions] = useGlobalChatState();

  // User app data hook for gem count (singleton) - only after auth loading completes
  const [userAppDataState, userAppDataActions] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );

  // Buffer to accumulate complete streaming content for database sync
  const streamingContentBuffer = useRef<string>('');
  
  // State to track if we're waiting for first token
  const [receivedFirstToken, setReceivedFirstToken] = useState(true);

  // Handle streaming text from API (merged from useReceiveStreamingTextFromAPI)
  const handleStreamingTextFromAPI = useCallback(async (
    streamType: 'START' | 'DONE' | 'INTERRUPTED' | 'CONTENT',
    content: string = '',
    messageId: string = ''
  ) => {
    // Set receivedFirstToken to true when we get content
    if (streamType === 'CONTENT') {
      setReceivedFirstToken(true);
    }
    try {
      // Handle frontend streaming text (similar to old LiveKit frontend_stream handling)
      if (streamType === 'START') {
        // Start a new streaming message
        globalChatStateActions.setStreamingMessageId(messageId);

        // Clear buffer on start
        streamingContentBuffer.current = '';

        // Add placeholder message
        messageActions.addMessageFrontendOnly({
          id: messageId,
          content: '',
          role: 'assistant',
          sender_id: avatarId,
          sender_name: avatarName,
          created_at: new Date().toISOString(),
          imageUrl: '',
          isStreaming: true,
        });

      } else if (streamType === 'DONE' || streamType === 'INTERRUPTED') {
        // Finalize the streaming message
        if (messageId) {
          // Get the current message to retrieve the complete content
          const currentMessage = messageActions.findMessage ? messageActions.findMessage(messageId) : null;
          const completeContent = currentMessage?.content || '';

          // Update streaming status in frontend
          messageActions.updateMessage(messageId, undefined, undefined, false);

          // Finalize the streaming message in database
          if (currentMessage) {
            const finalMessage = {
              ...currentMessage,
              content: completeContent,
              isStreaming: false,
            };
            messageActions.addMessageDBOnly(finalMessage);
          }

          globalChatStateActions.setStreamingMessageId('');

          // Clear buffer on done
          streamingContentBuffer.current = '';
        }
      } else if (streamType === 'CONTENT') {
        // Update the streaming message in real-time
        console.log('!! received API streaming text: ', content, ' messageId: ', messageId);

        if (messageId) {
          // Append current text to the streaming message
          // The message content will be used for finalization
          messageActions.appendMessage(messageId, content);
        }
      }
    } catch (error) {
      console.error('Error processing streaming text from API:', error);
    }
  }, [
    messageActions,
    globalChatStateActions,
    avatarId,
    avatarName
  ]);

  // Handle hint response from API (merged from useReceiveStreamingTextFromAPI)
  const handleHintResponseFromAPI = useCallback((hintText: string) => {
    try {
      // Parse hints using the same utility as the deprecated code
      const parsedHints = parseHints(hintText);
      
      // Update global state with parsed hints
      globalChatStateActions.setHints(parsedHints);
      globalChatStateActions.setHintLoading(false);
      globalChatStateActions.setCanGenerateHint(false); // Flip to false after receiving hints
      
      console.log('Received hints:', parsedHints);
    } catch (error) {
      console.error('Error processing hint response from API:', error);
      globalChatStateActions.setHintLoading(false);
    }
  }, [globalChatStateActions]);

  const sendText2API = useCallback(async (
    messageContentToSend: string
  ) => {
    if (!userEmail) {
      return { success: false, error: 'authentication_required' };
    }
    const isPlayBack = globalChatStateData.debugPlayBack;
    const isEmptyMessage = messageContentToSend.trim() === '';
    const messageType = isPlayBack ? 'play_back' : isEmptyMessage ? 'empty_message' : 'message';

    // Check if user has sufficient credits before sending message
    try {
      const currentModelPrice = getCurrentModelPrice(globalChatStateData.userChatSetting.languageModel);
      const totalPrice = currentModelPrice;

      // Use singleton gem count for faster access
      if (userAppDataState.creditCount + userAppDataState.dailyCreditCount < totalPrice) {
        // Return error object for insufficient credits
        return { success: false, error: 'insufficient_credits' };
      }
    } catch (error) {
      console.error('Error checking credits:', error);
      // If credit check fails, still allow sending message (fail-safe approach)
    }

    // Generate user message ID for frontend state management
    const userMessageId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Get current LLM model from chat settings
      const currentModel = globalChatStateData.userChatSetting.languageModel;

      // Get full message history for API call (last 20 messages)
      const messagesForAPI = getMessagesForAPI(20);

      // Ensure locale is always defined before creating payload
      const currentLocale: 'en' | 'zh-Hans' = locale || 'en';

      // Prepare API payload based on message type
      let apiPayload: any;

      if (messageType === 'empty_message') {
        // Empty message - request another response from LLM
        // Don't add to frontend state, just send to API
        apiPayload = {
          type: 'empty_message',
          messages: messagesForAPI,
          model: currentModel,
          userMessageId, // Send user message ID as reference
          avatarName,
          characterPrompt,
          userPreferredName,
          locale: currentLocale
        };
      } else if (messageType === 'play_back') {
        // Play back - no message content, just send to API
        apiPayload = {
          type: 'play_back',
          messages: messagesForAPI,
          model: currentModel,
          userMessageId, // Send user message ID as reference
          avatarName,
          characterPrompt,
          userPreferredName,
          locale: currentLocale
        };
      } else {
        // Regular message - add to frontend state first
        messageActions.addMessage({
          id: userMessageId,
          content: messageContentToSend,
          role: 'user',
          created_at: new Date().toISOString(),
          sender_id: userEmail,
          sender_name: userName ?? 'User',
          isStreaming: false,
        });

        // Then prepare API payload with the new message included (last 20 messages)
        apiPayload = {
          type: 'message',
          messages: messagesForAPI,
          model: currentModel,
          userMessageId, // Send user message ID as reference
          content: messageContentToSend,
          avatarName,
          characterPrompt,
          userPreferredName,
          locale: currentLocale
        };
      }

      // Set receivedFirstToken to false before sending
      setReceivedFirstToken(false);

      // Final safety check - ensure locale is always set
      if (!apiPayload.locale || apiPayload.locale === undefined) {
        apiPayload.locale = currentLocale;
      }

      // Call the API for streaming response
      const response = await fetch(`/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload),
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      // Consume the streaming response directly
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantMessageId = '';

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.trim() === '') continue;

              if (line.startsWith('data: ')) {
                const dataStr = line.slice(6);

                if (dataStr === '[DONE]') {
                  // Finalize the streaming message
                  handleStreamingTextFromAPI('DONE', '', assistantMessageId || userMessageId);
                  break;
                }

                try {
                  const data = JSON.parse(dataStr);

                  if (data.type) {
                    const streamType = data.type.toUpperCase() as 'START' | 'CONTENT' | 'DONE' | 'INTERRUPTED';

                    // Store the assistant message ID when we receive the START event
                    if (streamType === 'START') {
                      assistantMessageId = data.messageId;
                    }

                    handleStreamingTextFromAPI(streamType, data.content || '', data.messageId || assistantMessageId || userMessageId);
                  }
                } catch (e) {
                  // Ignore malformed JSON lines
                  continue;
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }

      // Deduct credits after successful API call
      try {
        const currentModelPrice = getCurrentModelPrice(globalChatStateData.userChatSetting.languageModel);
        await userAppDataActions.subtractGems(currentModelPrice);
        console.log(`Deducted ${currentModelPrice} credits for text message using model ${currentModel}`);
      } catch (error) {
        console.error('Error deducting credits:', error);
        // Don't block the user if credit deduction fails
      }

      return { success: true, messageId: userMessageId };
    } catch (error) {
      console.error(`Error sending ${messageType} to API:`, error);
      return { success: false, error: 'send_failed' };
    }
  }, [
    userEmail,
    userName,
    messageActions,
    userAppDataState.creditCount,
    avatarId,
    handleStreamingTextFromAPI,
    locale,
    avatarName,
    characterPrompt,
    userPreferredName,
    globalChatStateData.userChatSetting.languageModel,
    globalChatStateData.debugPlayBack,
    userAppDataActions,
    getMessagesForAPI
  ]);

  const getHintFromAPI = useCallback(async () => {
    // If we can't generate hint and panel is not shown, just toggle panel
    if (!globalChatStateData.canGenerateHint && !globalChatStateData.showHintPanel) {
      globalChatStateActions.setShowHintPanel(true);
      return;
    }
    
    // If we can't generate hint and panel is shown, just hide panel
    if (!globalChatStateData.canGenerateHint && globalChatStateData.showHintPanel) {
      globalChatStateActions.setShowHintPanel(false);
      return;
    }
    
    // If we can generate hint, request new hints
    try {
      globalChatStateActions.setHintLoading(true);
      globalChatStateActions.setShowHintPanel(true);
      globalChatStateActions.setHints([]);
      
      const messagesForHint = getMessagesForHintAPI();

      const apiPayload = {
        messages: messagesForHint,
        characterName: avatarName,
        userPreferredName: userPreferredName,
        locale: locale,
      };

      const response = await fetch('/api/chat/hint', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload),
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const data = await response.json();
      if (data.type === 'hint_response') {
        handleHintResponseFromAPI(data.text);
      }
    } catch (error) {
      console.error('Error getting hints from API:', error);
      globalChatStateActions.setHintLoading(false);
      globalChatStateActions.setShowHintPanel(false);
    }
  }, [
    avatarName,
    userPreferredName,
    handleHintResponseFromAPI,
    globalChatStateActions,
    locale,
    globalChatStateData.canGenerateHint,
    globalChatStateData.showHintPanel,
    getMessagesForHintAPI
  ]);

  // Helper function to add user message without sending to API (for "show me" messages)
  const addUserMessageOnly = useCallback(async (messageContent: string) => {
    if (!userEmail) {
      return { success: false, error: 'authentication_required' };
    }

    // Generate user message ID
    const userMessageId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      // Add user message to frontend state and database
      await messageActions.addMessage({
        id: userMessageId,
        content: messageContent,
        role: 'user',
        created_at: new Date().toISOString(),
        sender_id: userEmail,
        sender_name: userName ?? 'User',
        isStreaming: false,
      });

      return { success: true, messageId: userMessageId };
    } catch (error) {
      console.error('Error adding user message:', error);
      return { success: false, error: 'add_message_failed' };
    }
  }, [userEmail, userName, messageActions]);

  // Helper function to check if message is a "show me" request
  const isShowMeMessage = (message: string): { isShowMe: boolean; prompt: string } => {
    const trimmedMessage = message.trim();
    const lowerMessage = trimmedMessage.toLowerCase();

    if (lowerMessage.startsWith('show me')) {
      // Return the original message as the prompt
      return { isShowMe: true, prompt: trimmedMessage };
    }

    return { isShowMe: false, prompt: '' };
  };

  return { 
    sendText2API, 
    getHintFromAPI, 
    addUserMessageOnly, 
    isShowMeMessage,
    handleStreamingTextFromAPI,
    handleHintResponseFromAPI,
    receivedFirstToken
  };
}

/**
 * Get messages formatted for API call (similar to Python backend's get_llm_input_list)
 */
function getMessagesForAPI(maxMessages?: number) {
  // Get recent messages from LiveMessageDataHub (similar to Python's get_recent_messages)
  const messages = liveMessageDataHub.getMessages();

  // Limit to maxMessages if specified, otherwise return all
  const limitedMessages = maxMessages ? messages.slice(-maxMessages) : messages;

  // Convert to format expected by API (similar to Python backend)
  return limitedMessages.map(msg => ({
    role: msg.role,
    content: msg.content
  }));
}

function getMessagesForHintAPI() {
  const messages = liveMessageDataHub.getMessages();
  // Filter out image and default messages, similar to python backend
  const filteredMessages = messages.filter(msg => {
    const hasImageUrl = msg.imageUrl && msg.imageUrl.trim() !== '';
    const isDefaultImage = msg.id === 'default_image';
    return !hasImageUrl && !isDefaultImage;
  });

  // Limit to last 8 messages for hints
  const limitedMessages = filteredMessages.slice(-8);

  return limitedMessages.map(msg => ({
    role: msg.role,
    content: msg.content,
  }));
}
